import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_IDS } from "@/data/content";
import { useLanguage } from "@/i18n/LanguageContext";

const LangToggle = ({ lang, setLang, className = "" }) => (
    <div
        className={`flex items-center rounded-full border border-white/10 bg-white/[0.03] p-1 ${className}`}
        data-testid="lang-toggle"
        role="group"
        aria-label="Language selector"
    >
        {["en", "hu"].map((l) => (
            <button
                key={l}
                onClick={() => setLang(l)}
                aria-pressed={lang === l}
                className={`px-3 py-1 rounded-full font-mono text-xs uppercase tracking-wider transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 ${
                    lang === l
                        ? "bg-cyan-400 text-[#030305] font-bold"
                        : "text-zinc-400 hover:text-white"
                }`}
                data-testid={`lang-toggle-${l}`}
            >
                {l}
            </button>
        ))}
    </div>
);

export const Navbar = ({ onNavigate }) => {
    const { t, lang, setLang } = useLanguage();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const links = NAV_IDS.map((id, i) => ({ id, label: t.nav.links[i] }));

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        onNavigate(`#${id}`);
    };

    return (
        <motion.header
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
                scrolled
                    ? "backdrop-blur-xl bg-[#030305]/70 border-b border-white/5"
                    : "bg-transparent border-b border-transparent"
            }`}
            data-testid="main-nav"
        >
            <nav className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
                <button
                    onClick={() => go("home")}
                    className="font-display font-800 text-lg tracking-tight text-white shrink-0 mr-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 rounded"
                    data-testid="nav-logo"
                    aria-label="Overlast Studio home"
                >
                    OVERLAST<span className="text-cyan-400">.</span>STUDIO
                </button>

                <ul className="hidden lg:flex items-center gap-5 xl:gap-7">
                    {links.map((link) => (
                        <li key={link.id}>
                            <button
                                onClick={() => go(link.id)}
                                className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 hover:text-cyan-400 transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 rounded"
                                data-testid={`nav-link-${link.id}`}
                            >
                                {link.label}
                            </button>
                        </li>
                    ))}
                </ul>

                <div className="hidden lg:flex items-center gap-4">
                    <LangToggle lang={lang} setLang={setLang} />
                    <button
                        onClick={() => go("contact")}
                        className="group inline-flex items-center gap-2 rounded-full border border-cyan-400/40 bg-cyan-400/10 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-cyan-300 transition-colors duration-300 hover:bg-cyan-400 hover:text-[#030305] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
                        data-testid="nav-cta-button"
                    >
                        {t.nav.cta}
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                </div>

                <button
                    className="lg:hidden text-white p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 rounded"
                    onClick={() => setOpen(!open)}
                    aria-label={open ? "Close menu" : "Open menu"}
                    data-testid="nav-mobile-toggle"
                >
                    {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:hidden overflow-hidden backdrop-blur-xl bg-[#030305]/90 border-b border-white/5"
                        data-testid="nav-mobile-menu"
                    >
                        <ul className="px-6 py-6 space-y-4">
                            {links.map((link, i) => (
                                <motion.li
                                    key={link.id}
                                    initial={{ opacity: 0, x: -16 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: i * 0.05 }}
                                >
                                    <button
                                        onClick={() => go(link.id)}
                                        className="font-display text-2xl font-bold text-zinc-200 hover:text-cyan-400 transition-colors duration-300"
                                        data-testid={`nav-mobile-link-${link.id}`}
                                    >
                                        {link.label}
                                    </button>
                                </motion.li>
                            ))}
                        </ul>
                        <div className="px-6 pb-6">
                            <LangToggle lang={lang} setLang={setLang} />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
};
